
import { useCallback, useMemo, useState } from "react";
import NavBar from "@/components/NavBar";
import UserProfileCard from "@/components/home/UserProfileCard";
import QuickStats from "@/components/home/QuickStats";
import ActionButtons from "@/components/home/ActionButtons";
import ActiveGames from "@/components/home/active-games/ActiveGames";
import BirdieBoard from "@/components/home/BirdieBoard";
import { useFriendships } from "@/hooks/useFriendships";
import { useHomeUser } from "@/hooks/useHomeUser";
import { useHomeGames } from "@/hooks/useHomeGames";
import { useLoneWolf } from "@/hooks/useLoneWolf";
import { useRecurringGames } from "@/hooks/useRecurringGames";
import { useProcessInvites } from "@/hooks/useProcessInvites";
import { useSampleGame } from "@/hooks/useSampleGame";
import { useHomeBirdies } from "@/hooks/useHomeBirdies";
import { AuthLoadingScreen } from "@/context/AuthContext";
import PwaInstallBanner from "@/components/pwa/PwaInstallBanner";

const Home = () => {
  const { currentUser, isLoadingUser } = useHomeUser();
  const { loneWolfVersion } = useLoneWolf(currentUser?.homeCourseId ?? null);
  const { recurringVersion } = useRecurringGames();
  const { sampleGameVersion } = useSampleGame();
  useProcessInvites();

  const { friends, pendingRequests, sentRequests } = useFriendships();

  const userId = currentUser?.id;
  const { userGames, isLoadingGames } = useHomeGames(userId, loneWolfVersion + recurringVersion + sampleGameVersion);

  // Create a stable list of users for birdie data, including all types of connections
  const usersForBirdieData = friends.concat(
    pendingRequests,
    sentRequests,
    currentUser ? [currentUser] : []
  );

  // Deduplicate by user ID
  const uniqueUsers = Array.from(
    new Map(usersForBirdieData.map(user => [user.id, user])).values()
  );

  // Toggle for showing sample data on birdie board
  const [showSampleData, setShowSampleData] = useState(false);

  // Check if user has an active (non-archived) sample game
  const hasActiveSampleGame = useMemo(() =>
    userGames.some(g => g.isSample && !g.isArchived),
    [userGames]
  );

  const {
    birdieRecords,
    isLoadingBirdies,
    selectedYear,
    setSelectedYear,
  } = useHomeBirdies(currentUser, uniqueUsers, showSampleData);

  // Always show current year stats, independent of birdie board year selector
  const currentYear = new Date().getFullYear();
  const myCurrentYearRecords = useMemo(() => {
    if (!userId) return [];
    return birdieRecords.filter(r =>
      String(r.userId) === userId && new Date(r.date).getFullYear() === currentYear
    );
  }, [userId, birdieRecords, currentYear]);

  const yearBirdies = myCurrentYearRecords.filter(r => r.scoreType === 'birdie').length;
  const yearEagles = myCurrentYearRecords.filter(r => r.scoreType === 'eagle' || r.scoreType === 'double_eagle').length;

  // Count active (non-archived) games
  const activeGamesCount = useMemo(() =>
    userGames.filter(g => !g.isArchived).length,
    [userGames]
  );

  // Find latest birdie for current user with course name from games
  const latestBirdie = useMemo(() => {
    if (!userId) return null;
    const userBirdies = birdieRecords
      .filter(r => String(r.userId) === userId)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    if (userBirdies.length === 0) return null;

    const latest = userBirdies[0];
    // Build courseId → courseName map from games
    const courseMap = new Map(userGames.map(g => [g.courseId, g.courseName || ""]));
    return {
      hole: latest.holeNumber,
      course: courseMap.get(latest.courseId) || "",
    };
  }, [userId, birdieRecords, userGames]);

  const handleYearChange = useCallback((year: number) => {
    setSelectedYear(year);
  }, [setSelectedYear]);

  if (isLoadingUser) {
    return <AuthLoadingScreen />;
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen pb-16 flex items-center justify-center bg-cream">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Unable to load profile</h2>
          <p className="text-muted-foreground">Please sign out and sign back in.</p>
        </div>
        <NavBar />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16 bg-cream">
      <UserProfileCard currentUser={currentUser} />

      <QuickStats
        yearBirdies={yearBirdies}
        yearEagles={yearEagles}
        activeGamesCount={activeGamesCount}
        latestBirdie={latestBirdie}
        isLoading={isLoadingGames || isLoadingBirdies}
      />

      <ActionButtons />

      <ActiveGames
        userGames={userGames}
        isLoading={isLoadingGames}
        currentUserId={userId}
      />

      <BirdieBoard
        users={uniqueUsers}
        birdieRecords={birdieRecords}
        initialYear={selectedYear}
        onYearChange={handleYearChange}
        isLoading={isLoadingBirdies}
        showSampleToggle={hasActiveSampleGame}
        showSampleData={showSampleData}
        onToggleSampleData={setShowSampleData}
      />

      <PwaInstallBanner />
      <NavBar />
    </div>
  );
};

export default Home;
